/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { message } from 'antd';

import { PropTypes } from 'prop-types';
import CardGrid from '../common/cardGrid/cardGrid';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import FilterDropdown from '../common/FilterDropdown';
import WarningHeader from '../common/text/WarningHeader';

import FormsAddView from './FormsAddView';
import FormTemplateCard from './FormTemplateCard';
import { getFilterData, getDefaultFilters } from './formsFilters';

import {
  createTemplate,
  archiveTemplate,
  editTemplate,
  deleteTemplate,
  copyFormTemplate,
} from './state/forms.actions';

import {
  getProjectFileTemplates,
  getProjectCustomFieldTemplate,
} from '../projects/state/projects.actions';

import {
  getTemplates as getCostcodeTemplates,
  getCostcodeCustomFieldTemplate,
} from '../costcodes/state/costcodes.actions';

import { getUserCustomFieldTemplate } from '../users/state/users.actions';
import { getCustomerCustomFieldTemplate } from '../contacts/customers/state/customers.actions';
import {
  getCertificationCustomFieldTemplate,
  getCertificationTypes,
} from '../certifications/state/certifications.actions';
import { getBoardStatuses } from '../boards/state/boards.actions';

import {
  FORM_CREATE_TYPE,
} from '../nux/nux.constants';

import {
  createNuxEntry,
} from '../nux/state/nux.actions';
import { getFormLibrary } from './FormLibrary/state/formLibrary.actions';

import { storageUpgradeWarning } from '../files/fileHelpers';

import { parseDrawOptions } from '../common/pdf/PDFDesigner/PDFDesignerHelpers';

import Permissions from '../auth/Permissions';
import sortByString from '../helpers/helpers';
import {
  removeAPIFieldsFromSections,
  getInvalidMultiDivisionFields,
  getInvalidMultiDivisionWorkflowNodes,
  isMultiDivisionForm,
  resolveFormDivisionIds,
} from './formHelpers';
import { getFormTemplateDisplayColumns } from './forms.constants';

const toExternalFormSettingsText = (texts) => {
  if (!Array.isArray(texts)) return texts;
  return texts.map((text) => (String(text ?? '')));
};

const getFormMappings = async (id) => {
  try {
    const { data } = await axios.get(`/forms/templates/${id}/mappings`);
    return data ?? {};
  } catch (err) {
    message.error('Failed to get form workflow mappings');
    return [];
  }
};

const FIELDS_BY_STEP = [
  ['name', 'typeId', 'divisionIds'],
];

export default function FormTemplates({ history, projectId }) {
  const dispatch = useDispatch();
  const divisions = useSelector((state) => state.settings.divisions);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const formTypes = useSelector((state) => state.forms.types);

  const [formData, setFormData] = useState();
  const [disableNext, setDisableNext] = useState({});
  const [activeFilters, setActiveFilters] = useState({ active: new Set([1]), typeId: new Set() });

  const defaultFilterData = useMemo(() => getDefaultFilters(formTypes), [formTypes]);
  const filterData = useMemo(() => getFilterData(formTypes), [formTypes]);

  const useRedesign = settings?.uxRedesignPt1;
  const { templateArray, formTemplates, isLoading } = useSelector((state) => {
    const {
      forms: {
        templates = {},
        templatesIsLoading = true,
      } = {},
    } = state;
    const tempArr = Object.values(templates);
    tempArr.sort(sortByString('name'));
    return {
      formTemplates: templates,
      templateArray: tempArr,
      isLoading: templatesIsLoading,
    };
  });

  const nux = useSelector((state) => state.nux.nux);

  const {
    hasDocManagement,
    isOwner,
  } = useSelector((state) => {
    const {
      settings: {
        company: {
          userId: ownerId,
          documentManagementEnabled,
        } = {},
      } = {},
    } = state;
    return {
      hasDocManagement: documentManagementEnabled,
      isOwner: ownerId === Permissions.id,
    };
  });

  useEffect(() => {
    setActiveFilters(defaultFilterData);
  }, [defaultFilterData]);

  useEffect(() => {
    if (!projectId) dispatch(getFormLibrary());
  }, [dispatch, projectId]);

  useEffect(() => {
    dispatch(getProjectFileTemplates());
    dispatch(getCostcodeTemplates());
    dispatch(getUserCustomFieldTemplate());
    dispatch(getCostcodeCustomFieldTemplate());
    dispatch(getProjectCustomFieldTemplate());
    dispatch(getCustomerCustomFieldTemplate());
    dispatch(getCertificationCustomFieldTemplate('users'));
    dispatch(getCertificationTypes('users'));
    dispatch(getBoardStatuses());
  }, []);

  const onAdd = useCallback(async ({
    exportLocation,
    name,
    divisionId,
    divisionIds,
    typeId,
    externalFormPrimaryColor,
    externalFormAccentColor,
    externalFormSecondaryAccentColor,
    externalFormRedirectLink,
    externalFormText,
    pdfVersion,
    // externalFormPassword,
    // externalFormPasswordConfirm,
  } = {}) => {
    const {
      drawOptions,
      externalFormURL,
      sections,
      isExternalForm,
      displayFields,
      generateQRCode,
      respectAssignedUsers,
      externalFormLogo,
      externalFormLogoFileType,
      ...data
    } = formData;

    const resolvedDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

    if (isMultiDivisionForm(resolvedDivisionIds)) {
      const invalidFields = getInvalidMultiDivisionFields(sections);
      const invalidWorkflowNodes = getInvalidMultiDivisionWorkflowNodes(data?.workflow);
      if (invalidFields.length || invalidWorkflowNodes.length) {
        message.error('Please remove unsupported fields and workflow steps before saving a multi-division form.');
        return false;
      }
    }

    const fullData = {
      name,
      exportLocation,
      divisionId: resolvedDivisionIds[0] ?? divisionId,
      divisionIds: resolvedDivisionIds,
      typeId,
      ...data,
      ...parseDrawOptions(drawOptions),
      projectId,
      sections: isExternalForm ? removeAPIFieldsFromSections(sections) : sections,
      isExternalForm,
      generateQRCode,
      respectAssignedUsers,
      pdfVersion,
      settings: {
        url: externalFormURL,
        // password: externalFormPassword,
        primaryColor: externalFormPrimaryColor,
        accentColor: externalFormAccentColor,
        secondaryAccentColor: externalFormSecondaryAccentColor,
        redirectLink: externalFormRedirectLink,
        logoFileType: externalFormLogoFileType,
        logoFile: externalFormLogo,
        texts: toExternalFormSettingsText(externalFormText),
      },
      displayFields: displayFields?.map(({ fieldId, orderIndex, columnKey }) => ({
        fieldId,
        orderIndex,
        columnKey,
      })),
    };

    if (!dispatch) return false;
    if (await dispatch(createTemplate(fullData))) {
      setFormData();
      if (!nux.has(FORM_CREATE_TYPE)) {
        dispatch(createNuxEntry(FORM_CREATE_TYPE));
      }
      return true;
    }
    return false;
  }, [formData, nux, projectId]);

  const onEdit = useCallback(async (id, {
    exportLocation,
    name,
    divisionId,
    divisionIds,
    typeId,
    externalFormPrimaryColor,
    externalFormAccentColor,
    externalFormSecondaryAccentColor,
    externalFormRedirectLink,
    externalFormText,
    // externalFormPassword,
    // externalFormPasswordConfirm,
  } = {}) => {
    const {
      [id]: oldTemplate,
    } = formTemplates;
    if (!oldTemplate) return false;
    const newName = name || oldTemplate.name;
    const newType = typeId || oldTemplate.typeId;

    const {
      drawOptions,
      externalFormURL,
      sections,
      isExternalForm,
      displayFields,
      generateQRCode,
      respectAssignedUsers,
      externalFormLogo,
      externalFormLogoFileType,
      ...data
    } = formData;

    const resolvedDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

    if (isMultiDivisionForm(resolvedDivisionIds)) {
      const invalidFields = getInvalidMultiDivisionFields(sections);
      const invalidWorkflowNodes = getInvalidMultiDivisionWorkflowNodes(data?.workflow);
      if (invalidFields.length || invalidWorkflowNodes.length) {
        message.error('Please remove unsupported fields and workflow steps before saving a multi-division form.');
        return false;
      }
    }

    const fullData = {
      ...data,
      ...parseDrawOptions(drawOptions),
      name: newName,
      divisionId: resolvedDivisionIds[0] ?? divisionId,
      divisionIds: resolvedDivisionIds,
      typeId: newType,
      exportLocation,
      sections: isExternalForm ? removeAPIFieldsFromSections(sections) : sections,
      isExternalForm,
      generateQRCode,
      respectAssignedUsers,
      settings: {
        url: externalFormURL,
        // password: externalFormPassword,
        primaryColor: externalFormPrimaryColor,
        accentColor: externalFormAccentColor,
        secondaryAccentColor: externalFormSecondaryAccentColor,
        redirectLink: externalFormRedirectLink,
        logoFileType: externalFormLogoFileType,
        logoFile: externalFormLogo,
        texts: toExternalFormSettingsText(externalFormText),
      },
      displayFields: displayFields?.map(({ fieldId, orderIndex, columnKey }) => ({
        fieldId,
        orderIndex,
        columnKey,
      })),
    };

    if (!dispatch) return false;
    if (await dispatch(editTemplate(id, fullData))) {
      setFormData();
      return true;
    }
    return false;
  }, [formData, formTemplates]);

  const onArchive = useCallback(async (template) => {
    const mode = template.active ? 'Archive' : 'Activate';
    const formMappings = await getFormMappings(template.id);
    const content = (formMappings?.boardMappings?.length || formMappings?.equipmentMappings?.length)
      ? (
        <div>
          Warning: this form workflow is attached to the following mappings
          <ul style={{ margin: 10, textAlign: 'left' }}>
            {formMappings?.boardMappings.map((bfm) => (
              <li>
                <b style={{ color: 'black' }}>Board:</b> {bfm.board} <b style={{ color: 'black' }}>Mapping:</b> {bfm.name}
              </li>
            ))}
            {formMappings?.equipmentMappings.map((efm) => (
              <li>
                <b style={{ color: 'black' }}>Equipment Type:</b> {efm.equipmentType} <b style={{ color: 'black' }}>Mapping:</b> {efm.name}
              </li>
            ))}
          </ul>
          Archiving this form workflow will delete these mappings
        </div>
      )
      : null;
    CustomConfirmModal({
      title: `${mode} Form Workflow '${template.name}'?`,
      okText: mode,
      cancelText: 'Cancel',
      content,
      async onOk() {
        dispatch(archiveTemplate(template.id, !template.active));
      },
    });
  }, [dispatch]);

  const onDelete = useCallback(({ id, name }) => {
    if (!dispatch) return;
    CustomConfirmModal({
      title: `Delete Form Workflow '${name}'?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        dispatch(deleteTemplate(id));
      },
    });
  }, [dispatch]);

  const onFilter = useCallback((checkedFilters, key) => {
    const newFilters = { ...activeFilters };
    newFilters[key] = new Set(checkedFilters);
    setActiveFilters(newFilters);
  }, [activeFilters]);

  const filterIsAtDefault = useMemo(() => {
    const { active, typeId } = activeFilters;
    return active.has(1) && !active.has(0) && formTypes.every((type) => typeId.has(type.id));
  }, [activeFilters, formTypes]);

  const data = useMemo(() => (
    templateArray.filter((template) => (
      template.notDeleted
      && ((!projectId && !template.projectId) || template.projectId === projectId)
      && activeFilters.active.has(template.active)
      && activeFilters.typeId.has(template.typeId)
      && (
        selectedDivisions.has(template.divisionId)
        || template.divisionIds?.some((divId) => selectedDivisions.has(divId))
      )
    ))
  ), [activeFilters, templateArray, selectedDivisions, projectId]);

  const hasWritePerms = Permissions.has('FORMS_WRITE');

  const addConfig = useMemo(() => {
    if (!hasWritePerms) return null;
    return {
      title: 'Enter Form Details',
      testId: 'templateAdd',
      formView: (form) => (
        <FormsAddView
          {...form}
          onFormDataChange={setFormData}
          canAddGlobal={!!projectId}
          canAddFromLibrary={!projectId}
          projectId={projectId}
          setDisableNext={setDisableNext}
        />
      ),
      width: 'fit-content',
      onClose: onAdd,
      numberOfSteps: 4,
      disabled: !hasDocManagement,
      disabledPopoverTitle: <WarningHeader />,
      disabledPopoverContent: storageUpgradeWarning({
        isOwner,
        action: 'create custom forms',
      }),
      validateSteps: FIELDS_BY_STEP,
    };
  }, [hasWritePerms, onAdd, hasDocManagement, isOwner, projectId]);

  const onCopy = useCallback((e, { id }) => {
    e.stopPropagation();
    dispatch(copyFormTemplate(id));
  }, []);

  const displayColumns = useMemo(() => getFormTemplateDisplayColumns({
    useRedesign,
    divisions,
    hasWritePerms,
    onCopy,
  }), [useRedesign, divisions, hasWritePerms, onCopy]);

  const clickProps = useCallback(({ id, name, active, preview, type, typeId }) => ({
    id, name, active, preview, type, typeId,
  }), []);

  if (!Permissions.has('FORMS_READ')) {
    history.replace('/dashboard');
    return '';
  }

  return (
    <CardGrid
      dataSource={data}
      isLoading={isLoading}
      itemView={useRedesign ? null : FormTemplateCard}
      itemDimensions={{ width: 285, height: 197 }}
      colCount={3}
      pageSize={12}
      add={addConfig}
      isListView={useRedesign}
      displayColumns={displayColumns}
      testId="formsTemplatesSearch"
      edit={{
        title: 'Edit Form Details',
        formView: (form, formProps) => (
          <FormsAddView
            {...form}
            formProps={formProps}
            onFormDataChange={setFormData}
            projectId={projectId}
            setDisableNext={setDisableNext}
          />
        ),
        width: 'fit-content',
        onClose: onEdit,
        numberOfSteps: 4,
        canEdit: () => Permissions.has('FORMS_WRITE'),
        canArchive: () => Permissions.has('FORMS_WRITE'),
        canDelete: () => Permissions.has('FORMS_WRITE'),
        alwaysReturn: true,
        validateSteps: FIELDS_BY_STEP,
      }}
      onArchive={onArchive}
      onDelete={onDelete}
      filter={(
        <FilterDropdown
          filters={[filterData]}
          activeFilters={activeFilters}
          onFilter={onFilter}
          style={{ width: 350 }}
          useRedesign={useRedesign}
        />
        )}
      filterActive={filterIsAtDefault}
      disableNext={disableNext}
      setDisableNext={setDisableNext}
      title="Form Workflows"
      clickProps={clickProps}
    />
  );
}

FormTemplates.propTypes = {
  history: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
};
